import { SectionHeading } from '@/components/PartFinder/SectionHeading';
import { ExpandMore } from '@mui/icons-material';
import { Box, ButtonBase, Collapse } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ReactNode, useState } from 'react';

const SectionBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: '12px',
  marginBottom: '16px',
  width: '100%',
  maxWidth: '1600px',
}));

const HeaderButton = styled(ButtonBase)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  textAlign: 'left',
  borderRadius: '4px',
});

const HeadingSlot = styled(Box)({
  flex: 1,
  minWidth: 0,
  '& > *': {
    marginBottom: 0,
  },
});

const ChevronWrapper = styled(Box, { shouldForwardProp: property => property !== 'expanded' })<{
  expanded: boolean;
}>(({ theme, expanded }) => ({
  display: 'flex',
  alignItems: 'center',
  marginLeft: '8px',
  color: theme.palette.text.secondary,
  transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

const Body = styled(Box)({
  marginTop: '8px',
});

export const CollapsibleSection = ({
  icon,
  title,
  tooltip,
  defaultExpanded = true,
  expanded: expandedProperty,
  onExpandedChange,
  children,
}: {
  icon: ReactNode;
  title: string;
  tooltip?: string;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  children: ReactNode;
}) => {
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(defaultExpanded);
  const isControlled = expandedProperty !== undefined;
  const expanded = isControlled ? expandedProperty : uncontrolledExpanded;
  const handleToggle = () => {
    const next = !expanded;
    if (!isControlled) setUncontrolledExpanded(next);
    onExpandedChange?.(next);
  };
  return (
    <SectionBox>
      <HeaderButton
        onClick={handleToggle}
        aria-expanded={expanded}
        aria-label={`${expanded ? 'Collapse' : 'Expand'} ${title}`}
      >
        <HeadingSlot>
          <SectionHeading icon={icon} title={title} tooltip={tooltip} />
        </HeadingSlot>
        <ChevronWrapper expanded={expanded}>
          <ExpandMore fontSize='small' />
        </ChevronWrapper>
      </HeaderButton>
      <Collapse in={expanded} timeout='auto' unmountOnExit>
        <Body>{children}</Body>
      </Collapse>
    </SectionBox>
  );
};
